import React, { useEffect, useLayoutEffect, useRef } from 'react'

import { useRouter } from 'next/router'

import ChangelogEntryCard, {
  changelogEntryDomId,
} from '@/components/changelog/ChangelogEntry'

import { changelogEntries } from '@/changelog/registry'

import styles from './styles.module.css'

const changelogEntryIds = new Set(changelogEntries.map((entry) => entry.id))

const getEntryParam = (value: string | string[] | undefined): string | null => {
  if (Array.isArray(value)) return value[0] ?? null
  return value ?? null
}

const knownEntryParam = (
  value: string | string[] | undefined,
): string | null => {
  const entryId = getEntryParam(value)
  if (entryId === null || !changelogEntryIds.has(entryId)) return null
  return entryId
}

const SCROLL_SETTLE_MS = 1800
const SCROLL_RETRY_DELAYS_MS = [100, 250, 500, 900, 1400]
const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

const getScrollContainer = (target: HTMLElement): HTMLElement => {
  let element = target.parentElement

  while (element && element !== document.body) {
    const { overflowY } = window.getComputedStyle(element)
    const canScroll = /(auto|scroll|overlay)/.test(overflowY)

    if (canScroll && element.scrollHeight > element.clientHeight) {
      return element
    }

    element = element.parentElement
  }

  return document.scrollingElement instanceof HTMLElement
    ? document.scrollingElement
    : document.documentElement
}

const scrollToChangelogEntry = (target: HTMLElement) => {
  const container = getScrollContainer(target)
  const anchor =
    target.querySelector<HTMLElement>('[data-changelog-entry-anchor]') ?? target
  const targetRect = anchor.getBoundingClientRect()
  const containerRect =
    container === document.documentElement
      ? { top: 0 }
      : container.getBoundingClientRect()
  const scrollMarginTop = Number.parseFloat(
    window.getComputedStyle(anchor).scrollMarginTop || '0',
  )

  container.scrollTo({
    top:
      container.scrollTop +
      targetRect.top -
      containerRect.top -
      scrollMarginTop,
    behavior: 'auto',
  })
}

const shouldCancelDeepLinkScroll = (event: KeyboardEvent) =>
  [' ', 'ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp'].includes(
    event.key,
  )

export const ChangelogPageContent: React.FC = () => {
  const router = useRouter()
  const pageRootRef = useRef<HTMLDivElement | null>(null)
  const lastQueryScrollRef = useRef<string | null>(null)
  const queryEntryId = knownEntryParam(router.query.entry)

  useBrowserLayoutEffect(() => {
    if (!router.isReady || queryEntryId === null) return
    if (lastQueryScrollRef.current === queryEntryId) return

    const target = document.getElementById(changelogEntryDomId(queryEntryId))
    if (!target) return

    lastQueryScrollRef.current = queryEntryId
    const scrollContainer = getScrollContainer(target)
    let isCancelled = false
    let rafId: number | null = null
    const timeoutIds: number[] = []

    const cancelPendingAlign = () => {
      isCancelled = true
      observer?.disconnect()
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
        rafId = null
      }
      for (const timeoutId of timeoutIds) {
        window.clearTimeout(timeoutId)
      }
      timeoutIds.length = 0
    }

    const alignEntry = () => {
      if (isCancelled) return
      scrollToChangelogEntry(target)
    }

    const scheduleAlign = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = null
        alignEntry()
      })
    }

    scheduleAlign()

    for (const delay of SCROLL_RETRY_DELAYS_MS) {
      timeoutIds.push(window.setTimeout(scheduleAlign, delay))
    }

    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => scheduleAlign())

    const cancelOnKeyboard = (event: KeyboardEvent) => {
      if (shouldCancelDeepLinkScroll(event)) {
        cancelPendingAlign()
      }
    }

    scrollContainer.addEventListener('wheel', cancelPendingAlign, {
      passive: true,
    })
    scrollContainer.addEventListener('touchstart', cancelPendingAlign, {
      passive: true,
    })
    scrollContainer.addEventListener('pointerdown', cancelPendingAlign, {
      passive: true,
    })
    window.addEventListener('keydown', cancelOnKeyboard)

    if (observer && pageRootRef.current) {
      observer.observe(pageRootRef.current)
      timeoutIds.push(
        window.setTimeout(() => observer.disconnect(), SCROLL_SETTLE_MS),
      )
    }

    return () => {
      scrollContainer.removeEventListener('wheel', cancelPendingAlign)
      scrollContainer.removeEventListener('touchstart', cancelPendingAlign)
      scrollContainer.removeEventListener('pointerdown', cancelPendingAlign)
      window.removeEventListener('keydown', cancelOnKeyboard)
      cancelPendingAlign()
    }
  }, [queryEntryId, router.isReady])

  return (
    <div
      ref={pageRootRef}
      className={`w-full mx-auto px-4 md:px-8 py-10 md:py-16 ${styles.pageRoot}`}
    >
      {changelogEntries.length === 0 ? (
        <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-white dark:bg-default-dark px-4 py-6 text-primary-700 dark:text-primary-300">
          No changelog entries yet.
        </div>
      ) : (
        <div>
          {changelogEntries.map((entry) => (
            <ChangelogEntryCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  )
}

export default ChangelogPageContent
